import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Tabs,
} from '@mui/material'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import ListOfValuesComponent from 'features/expedientes/components/fields/ListOfValuesComponent'
import { SnackbarProvider, closeSnackbar, useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import useFetchCamposExpediente from '../hooks/useFetchCamposExpediente'
import { ExpedientesContext } from '../providers/ExpedientesProvider'
import DateComponent from './fields/DateComponent'
import FileComponent from './fields/FileComponent'
import TextComponent from './fields/TextComponent'

import TabContext from '@mui/lab/TabContext'
import TabPanel from '@mui/lab/TabPanel'

export default function ExpedienteForm() {
  const {
    active: { current: tipo_expediente, forceRefresh },
    campos_expediente,
    enviarExpediente,
    regresar,
    setCamposExpediente,
    setCamposOrigen,
    setCampoError,
    ficherosSubidos,
    setInicio,
    setMostrar,
    mensajeValidacion,
    setMensajeValidacion,
  } = useContext(ExpedientesContext)

  const {
    loading: loadingCampos,
    error,
    response,
  } = useFetchCamposExpediente({ expediente: tipo_expediente })

  const [openBackdrop, setOpenBackdrop] = useState(false)
  const [tabValue, setTabValue] = useState('form')
  const { enqueueSnackbar } = useSnackbar()
  const [filtros, setFiltros] = useState([])

  useEffect(() => {
    // Reiniciamos posible paginacion de listado
    setInicio(0)
    setMostrar(12)
  }, [])

  useEffect(() => {
    if (response) {
      setCamposExpediente(response.expediente.campos || [])
      setCamposOrigen(response.expediente.campos)
    }
  }, [response])

  // Recogida de valores para los filtros necesarios en las LoV
  useEffect(() => {
    const tempFiltros = campos_expediente.map((c) => ({
      codigo: c.columna,
      valor: c.valor,
    }))
    setFiltros(tempFiltros)
  }, [campos_expediente])

  useEffect(() => {
    setOpenBackdrop(loadingCampos ?? false)
  }, [loadingCampos])

  useEffect(() => {
    if (mensajeValidacion.mostrar) {
      enqueueSnackbar(mensajeValidacion.texto, { variant: mensajeValidacion.tipo })
      setMensajeValidacion({ tipo: '', texto: '', mostrar: false })
    }
  }, [mensajeValidacion])

  const handleValidateRequiredFields = () => {
    const requiredFields = campos_expediente.filter((c) => c.obligatorio === 'S')
    const missingFields = requiredFields.filter(
      (c) =>
        (c.tipo_input !== 'file' && !c.valor) ||
        (c.tipo_input === 'file' && ficherosSubidos.length === 0),
    )

    if (missingFields.length > 0) {
      enqueueSnackbar('Debe rellenar todos los campos obligatorios', { variant: 'error' })

      missingFields.forEach((campo) => {
        setCampoError(campo.columna, 'El campo es obligatorio')
      })

      const updatedCampos = campos_expediente.map((campo) => {
        const missingField = missingFields.find((mf) => mf.columna === campo.columna)
        return missingField ? { ...campo, error: 'El campo es obligatorio' } : campo
      })
      setCamposExpediente(updatedCampos)

      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!handleValidateRequiredFields()) return

    setOpenBackdrop(true)
    try {
      const response = await enviarExpediente()

      if (response?.data) {
        if (response?.data?.status === 'ERROR') {
          enqueueSnackbar(response?.data?.texto_mensaje, { variant: response?.data?.tipo_mensaje })
        } else {
          enqueueSnackbar(
            'Expediente ' + response?.data?.numero_expediente + ' creado correctamente',
            { variant: 'success' },
          )
        }
      }
    } catch (error) {
      enqueueSnackbar('Error al crear el expediente, inténtelo de nuevo más tarde', {
        variant: 'error',
      })
    } finally {
      forceRefresh()
      setOpenBackdrop(false)
    }
  }

  const hasFileFields =
    campos_expediente?.some((c) => c.tipo_input === 'file') &&
    tipo_expediente?.tipos_documentos?.length > 0

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  if (!tipo_expediente || !campos_expediente) {
    return null
  }

  return (
    <Box component="form" autoComplete="off">
      <SnackbarProvider
        maxSnack={1}
        autoHideDuration={6000}
        action={(snackbarId) => (
          <IconButton onClick={() => closeSnackbar(snackbarId)} aria-label="cerrar alerta">
            <CloseRoundedIcon />
          </IconButton>
        )}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant={'error'}
      />
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: 2,
          }}
        >
          <Button
            sx={{
              margin: '5px',
              display: { xs: 'none', md: 'flex' },
            }}
            startIcon={<ArrowBackIcon />}
            variant="contained"
            color="primary"
            onClick={() => regresar()}
          >
            Volver
          </Button>
        </Box>
        <Card sx={{ width: '100%' }}>
          <CardHeader title={'Alta de expediente - ' + tipo_expediente.nombre} sx={{ p: 5 }} />
          <CardContent sx={{ p: 5 }}>
            {loadingCampos ? (
              <>
                <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                  {/* Cabecera */}
                  <Box sx={{ mb: 3 }}>
                    <Skeleton variant="text" width={220} height={36} />
                    <Skeleton variant="text" width={360} height={24} />
                  </Box>

                  {/* Campos en una sola columna */}
                  {[...Array(8).keys()].map((_, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Box sx={{ mb: 1 }}>
                        <Skeleton variant="text" width={120} height={20} />
                      </Box>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={56}
                        sx={{ borderRadius: 1.5 }}
                      />
                    </Box>
                  ))}

                  {/* Acciones */}
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                    <Skeleton
                      variant="rectangular"
                      width={120}
                      height={40}
                      sx={{ borderRadius: 2 }}
                    />
                    <Skeleton
                      variant="rectangular"
                      width={160}
                      height={40}
                      sx={{ borderRadius: 2 }}
                    />
                  </Box>
                </Paper>
              </>
            ) : hasFileFields ? (
              <TabContext value={tabValue}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="form tabs"
                  sx={{ mb: 3 }}
                >
                  <Tab label="Formulario" value="form" />
                  <Tab label="Documentos" value="files" />
                </Tabs>

                <TabPanel value="form" sx={{ p: 0 }}>
                  <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                    {campos_expediente
                      .filter((c) => c.tipo_input !== 'file')
                      .map((c) => {
                        if (c.tipo_input === 'lv')
                          return (
                            <ListOfValuesComponent
                              key={c.columna}
                              columna={c.columna}
                              filtros={filtros}
                              error={c?.error}
                            />
                          )
                        if (
                          c.tipo_input === 'text' ||
                          c.tipo_input === 'number' ||
                          c.tipo_input === 'textarea'
                        )
                          return (
                            <TextComponent key={c.columna} columna={c.columna} error={c?.error} />
                          )
                        if (c.tipo_input === 'date')
                          return <DateComponent key={c.columna} columna={c.columna} />
                        return null
                      })}
                  </Grid>
                </TabPanel>

                <TabPanel value="files" sx={{ p: 0 }}>
                  <Grid container spacing={2}>
                    {campos_expediente
                      .filter((c) => c.tipo_input === 'file')
                      .map((c) => (
                        <FileComponent
                          key={c.columna}
                          columna={c.columna}
                          documentTypes={tipo_expediente.tipos_documentos}
                        />
                      ))}
                  </Grid>
                </TabPanel>
              </TabContext>
            ) : (
              <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                {campos_expediente.map((c) => {
                  if (c.tipo_input === 'lv')
                    return (
                      <ListOfValuesComponent
                        key={c.columna}
                        columna={c.columna}
                        filtros={filtros}
                        error={c?.error}
                      />
                    )
                  if (
                    c.tipo_input === 'text' ||
                    c.tipo_input === 'number' ||
                    c.tipo_input === 'textarea'
                  )
                    return <TextComponent key={c.columna} columna={c.columna} error={c?.error} />
                  if (c.tipo_input === 'date')
                    return <DateComponent key={c.columna} columna={c.columna} />
                  return null
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            pt: 2,
            width: '100%',
            margin: 'auto',
            marginBottom: '25px',
          }}
        >
          <Button onClick={handleSave} variant="contained" color="error" size="large">
            Enviar Expediente
          </Button>
        </Box>
      </Container>
    </Box>
  )
}
